import { CNPJValidate, CPFValidate } from "classes";
import {
  minDate,
  validAlphanumeric,
  validEmail,
  validPassword,
  validPhone,
} from "consts";
import { StepAttendanceEnum } from "modals/addAttendance/viewModel";
import { ControlTypeIDEnum, SubChannelTypeEnum } from "models";
import { timeIsSameOrAfter } from "utils";
import * as yup from "yup";

const timeTest = (key: string) =>
  function (this: yup.TestContext<yup.AnyObject>, end?: string) {
    const start = this.parent[key];
    const times = ["00:00", "00:00:00"];

    if (start) {
      if (times.includes(start) && end && times.includes(end)) return true;
      else if (end) return timeIsSameOrAfter(start, end);

      return false;
    }

    return true;
  };

// Defaults

const AlphanumericSchema = yup
  .string()
  .nonNullable()
  .transform((value, origin) => (origin === "" ? undefined : value))
  .matches(validAlphanumeric, "errors.invalidAlphanumeric");

const BooleanSchema = yup
  .boolean()
  .nonNullable()
  .transform((value) => value ?? false);

const BooleanRequiredSchema = BooleanSchema.required("errors.required");

const CNPJSchema = yup.string().test((value, ctx) => {
  if (!!value && !CNPJValidate.isValid(value))
    return ctx.createError({
      message: "errors.invalidCNPJ",
      type: "invalid",
    });

  return true;
});

const CPFSchema = yup.string().test((value, ctx) => {
  if (!!value && !CPFValidate.isValid(value))
    return ctx.createError({
      message: "errors.invalidCPF",
      type: "invalid",
    });

  return true;
});

const CPFOrCNPJSchema = yup.string().test((value, ctx) => {
  if (!!value && !CPFValidate.isValid(value) && !CNPJValidate.isValid(value))
    return ctx.createError({
      message: "errors.invalidCPFOrCNPJ",
      type: "invalid",
    });

  return true;
});

const DateSchema = yup
  .date()
  .nonNullable()
  .transform((value) =>
    !isNaN(value) && value instanceof Date ? value : undefined,
  );

const DateMaxSchema = DateSchema.min(
  minDate.split("T")[0],
  "errors.invalidMinDate",
).max(new Date().toISOString().split("T")[0], "errors.invalidMaxDate");

const DateRequiredSchema = DateSchema.required("errors.required");

const DateMaxRequiredSchema = DateMaxSchema.required("errors.required");

const DateTimeMinSchema = DateSchema.min(
  new Date(),
  "errors.invalidMinDateTime",
);

const DateTimeMinRequiredSchema = DateTimeMinSchema.required("errors.required");

const EmailSchema = yup
  .string()
  .nonNullable()
  .transform((value, origin) => (origin === "" ? undefined : value))
  .matches(validEmail, "errors.invalidEmail");

const EmailRequiredSchema = EmailSchema.required("errors.required");

const MixedSchema = yup.mixed<File>();

const MixedRequiredSchema = MixedSchema.required("errors.required");

const NumberSchema = yup
  .number()
  .nonNullable()
  .transform((value) => (isNaN(value) || value === null ? undefined : value));

const NumberRequiredSchema = NumberSchema.required("errors.required");

const NumberArraySchema = yup.array().of(yup.number().required()).optional();

const NumberArrayRequiredSchema = NumberArraySchema.required(
  "errors.required",
).min(1, "errors.required");

const PasswordSchema = yup
  .string()
  .required("errors.required")
  .matches(validPassword, "errors.invalidPassword");

const PhoneSchema = yup.string().matches(validPhone, {
  excludeEmptyString: true,
  message: "errors.invalidPhone",
});

const PhoneRequiredSchema = PhoneSchema.required("errors.required");

const StringSchema = yup.string().optional();

const StringRequiredSchema = StringSchema.required("errors.required");

const TimeSchema = yup.string();

const TimeRequiredSchema = TimeSchema.required("errors.required");

// Exports

export const AddAttendanceSchema = yup.object().shape({
  step: NumberRequiredSchema,
  name: StringSchema.when(
    ["step", "ddd", "phone", "cpfOrCNPJ", "contract", "user"],
    ([step, ddd, phone, cpfOrCNPJ, contract, user]) => {
      return step === StepAttendanceEnum.addContact ||
        (!ddd && !phone && !cpfOrCNPJ && !contract && !user)
        ? StringRequiredSchema
        : StringSchema;
    },
  ),
  ddd: StringSchema,
  phone: StringSchema.when(["step", "ddd"], ([step, ddd]) =>
    step === StepAttendanceEnum.addContact || !!ddd
      ? StringRequiredSchema
      : StringSchema,
  ),
  cpfOrCNPJ: CPFOrCNPJSchema,
  contract: StringSchema,
  channel: NumberSchema.when("step", ([step]) =>
    step !== StepAttendanceEnum.searchAttendance
      ? NumberRequiredSchema
      : NumberSchema,
  ),
  user: NumberSchema,
  folder: NumberSchema,
  start: DateMaxSchema.when("step", ([step]) =>
    step === StepAttendanceEnum.searchAttendance
      ? DateMaxRequiredSchema
      : DateMaxSchema,
  ),
  end: DateMaxSchema.when(["step", "start"], ([step, start]) =>
    step === StepAttendanceEnum.searchAttendance && start
      ? DateMaxRequiredSchema.min(yup.ref("start"), "errors.invalidMinDate")
      : DateMaxSchema,
  ),
});

export const BatchCampaignQueueSchema = yup.object().shape({
  queue: NumberRequiredSchema,
});

export const BatchCampaignSchema = yup.object().shape({
  active: BooleanRequiredSchema,
  queues: NumberArrayRequiredSchema,
  action: NumberRequiredSchema,
});

export const BatchOrderSchema = yup.object().shape({
  agent: NumberRequiredSchema,
});

export const BatchQueueSchema = yup.object().shape({
  active: BooleanRequiredSchema,
});

export const BatchUserChannelSchema = yup.object().shape({
  hasPriority: BooleanRequiredSchema,
  queues: NumberArrayRequiredSchema,
  action: NumberRequiredSchema,
  priority: NumberSchema.when("hasPriority", ([hasPriority]) =>
    hasPriority ? NumberRequiredSchema : NumberSchema,
  ),
});

export const BatchUserSchema = yup.object().shape({
  active: BooleanSchema,
  supervisor: NumberRequiredSchema,
  permission: NumberRequiredSchema,
  project: NumberRequiredSchema,
});

export const BatchStatusGroupSchema = yup.object().shape({
  orderStates: NumberArrayRequiredSchema,
  action: NumberRequiredSchema,
});

export const BatchTransferAttendanceSchema = yup.object().shape({
  step: NumberRequiredSchema,
  transferBy: NumberRequiredSchema,
});

export const ConferenceAttendanceSchema = yup.object().shape({
  step: NumberRequiredSchema,
  externalPhone: NumberRequiredSchema,
  ddd: StringSchema,
  phone: StringSchema,
});

export const CreatePasswordSchema = yup.object().shape({
  password: PasswordSchema,
});

export const DialAttendanceSchema = yup.object().shape({
  ddd: StringSchema,
  phone: StringRequiredSchema,
});

export const EndAttendanceSchema = yup.object().shape({
  isVoice: BooleanRequiredSchema,
  folder: NumberSchema.when("isVoice", ([isVoice]) =>
    !isVoice ? NumberRequiredSchema : NumberSchema,
  ),
  statusGroup: NumberRequiredSchema,
  status: NumberRequiredSchema,
  obsIsRequired: BooleanSchema,
  observation: StringSchema.when("obsIsRequired", ([obsIsRequired]) =>
    obsIsRequired ? StringRequiredSchema : StringSchema,
  ),
});

export const ForgotEmailSchema = yup.object().shape({
  phone: PhoneRequiredSchema,
});

export const ForgotPasswordSchema = yup.object().shape({
  email: EmailRequiredSchema,
});

export const HSMImportSchema = yup.object().shape({
  batchName: StringRequiredSchema,
  sendDate: DateTimeMinRequiredSchema,
  template: NumberRequiredSchema,
  file: MixedRequiredSchema,
});

export const ImportSchema = yup.object().shape({
  name: StringRequiredSchema,
  type: NumberRequiredSchema,
  file: MixedRequiredSchema,
});

export const ManageAttendGroupSchema = yup.object().shape({
  active: BooleanSchema,
  name: StringRequiredSchema,
  queue: NumberSchema,
  project: NumberSchema,
  dialingMode: NumberSchema,
  channelType: NumberSchema,
  omniSource: NumberSchema,
  amountPowerCalls: NumberSchema,
  minSecWaiting: NumberSchema,
  maxSecWaiting: NumberSchema,
  dropMax: NumberSchema,
  paused: BooleanSchema,
  activeForService: BooleanSchema,
  afinitiIntegration: BooleanSchema,
  virtualAgentQueue: BooleanSchema,
  alert: NumberSchema,
  secEmailAlertAWT: NumberSchema,
  sipServer: NumberSchema,
  codeAccount: NumberSchema,
  weekStart: NumberSchema,
  weekEnd: NumberSchema,
  saturdayStart: NumberSchema,
  saturdayEnd: NumberSchema,
  sundayStart: NumberSchema,
  sundayEnd: NumberSchema,
});

export const ManageCampaignAttendGroupSchema = yup.object().shape({
  active: BooleanSchema,
  campaign: NumberSchema,
  attendGroup: NumberSchema,
  weight: NumberSchema,
  percent: NumberSchema,
  filterType: NumberSchema,
  replicateFilterType: BooleanSchema,
  phoneType: NumberSchema,
  sequence: NumberSchema,
  renitenceGroup: NumberSchema,
  replicateRenitenceGroup: BooleanSchema,
  scoreGroup: NumberSchema,
  replicateScoreGroup: BooleanSchema,
  dialDirection: NumberSchema,
  transferAttendGroup: NumberSchema,
  virtualLocator: NumberSchema,
  sms: NumberSchema,
  cellPhone: NumberSchema,
});

export const ManageCampaignSchema = yup.object().shape({
  active: BooleanSchema,
  name: StringRequiredSchema,
  queue: NumberSchema,
});

export const ManageCampaignTypeSchema = yup.object().shape({
  active: BooleanSchema,
  name: StringRequiredSchema,
  form: NumberRequiredSchema,
});

export const ManageChannelSchema = yup.object().shape({
  type: NumberRequiredSchema,
  active: BooleanSchema,
  name: StringRequiredSchema,
  phone: StringSchema.when("type", ([type]) =>
    type === SubChannelTypeEnum.WhatsApp ? StringRequiredSchema : StringSchema,
  ),
  botId: StringSchema.when("type", ([type]) =>
    type !== SubChannelTypeEnum.WhatsApp ? StringRequiredSchema : StringSchema,
  ),
  salutation: StringSchema,
  finalizeAttend: StringSchema,
  clientDowntime: StringSchema,
  clientInactivity: StringSchema,
  automaticCompletionTime: StringSchema,
  automaticCompletion: StringSchema,
  messageWaitTime: StringSchema,
  messageWait: StringSchema,
  hasFinalizeMeta: BooleanSchema,
  finalizeMeta: StringSchema,
  campaignType: NumberRequiredSchema,
  callNumbers: StringSchema,
  messageNewAttendance: StringSchema,
  salesScript: StringSchema,
});

export const ManageCompanySchema = yup.object().shape({
  name: StringSchema,
  cnpj: CNPJSchema,
  responsibleName: StringSchema,
  email: StringSchema,
  password: StringSchema,
  confirmPassword: StringSchema,
});

export const ManageCustomTableSchema = yup.object().shape({
  active: BooleanSchema,
  name: StringRequiredSchema,
  associatedField: NumberSchema,
});

export const ManageDDDSchema = yup.object().shape({
  ddd: StringRequiredSchema,
  startTime: StringRequiredSchema,
  endTime: StringRequiredSchema,
  startTimeOnWeekend: StringSchema,
  endTimeOnWeekend: StringSchema,
  active: BooleanSchema,
});

export const ManageIntervalSchema = yup.object().shape({
  active: BooleanSchema,
  name: StringRequiredSchema,
  dsCode: NumberRequiredSchema,
});

export const ManageFAQSchema = yup.object().shape({
  active: BooleanSchema,
  question: StringRequiredSchema,
  response: StringRequiredSchema,
});

export const ManageFieldSchema = yup
  .object()
  .shape({
    template: NumberRequiredSchema,
    active: BooleanSchema,
    name: StringRequiredSchema,
    context: NumberRequiredSchema,
    controlType: NumberRequiredSchema,
    columnName: StringRequiredSchema,
    type: NumberRequiredSchema,
    controlColor: StringSchema,
    isRequired: BooleanSchema,
    regularExpression: StringSchema,
    messageRegex: StringSchema,
    mask: StringSchema,
    maxLength: NumberSchema.when("controlType", ([controlType]) =>
      controlType === ControlTypeIDEnum.TextBox
        ? NumberRequiredSchema.min(1, "errors.invalidMinNumber").max(
            500,
            "errors.invalidMaxNumber",
          )
        : NumberSchema,
    ),
    controlSize: NumberRequiredSchema,
    tableRef: StringSchema,
    fieldRef: StringSchema,
    descriptionRef: StringSchema,
    orderRef: StringSchema,
    alwaysActive: BooleanSchema,
    isReadonly: BooleanSchema,
    fieldOrder: NumberRequiredSchema,
  })
  .test(({ regularExpression, messageRegex }, ctx) => {
    if (!!regularExpression && !messageRegex)
      return new yup.ValidationError(
        "errors.required",
        messageRegex,
        "messageRegex",
      );
    else if (!!messageRegex && !regularExpression)
      return new yup.ValidationError(
        "errors.required",
        regularExpression,
        "regularExpression",
      );
    else return true;
  });

export const ManageFormSchema = yup.object().shape({
  active: BooleanSchema,
  name: StringRequiredSchema,
});

export const ManageHSMTemplateSchema = yup.object().shape({
  active: BooleanSchema,
  name: StringRequiredSchema,
  code: StringRequiredSchema,
  channel: NumberRequiredSchema,
  phrase: StringRequiredSchema,
  responseHook: StringSchema,
});

export const ManageProductSchema = yup.object().shape({
  active: BooleanSchema,
  name: StringRequiredSchema,
  value: StringSchema,
});

export const ManageProjectSchema = yup.object().shape({
  active: BooleanSchema,
  name: StringRequiredSchema,
  urlWebPhone: StringSchema,
  urlDialer: StringSchema,
  filterStatusByCampaignType: BooleanSchema,
  hsmFromVoice: BooleanSchema,
  makeCallFromWhatsapp: BooleanSchema,
  dialerQuery: BooleanSchema,
  hideQueryMailing: BooleanSchema,
  hideQueryOmni: BooleanSchema,
  hideInsertMailing: BooleanSchema,
  blindTransferAuto: BooleanSchema,
  ticketModule: BooleanSchema,
  transaction: BooleanSchema,
  questionnaireModule: BooleanSchema,
  videoModule: BooleanSchema,
  loginWebphone: BooleanSchema,
  prioritizeVoiceAttendance: BooleanSchema,
  hideNameGroup: BooleanSchema,
  mailingAttachment: BooleanSchema,
});

export const ManageQueueSchema = yup.object().shape({
  active: BooleanSchema,
  name: StringRequiredSchema,
  channelType: NumberRequiredSchema,
  omniSource: NumberRequiredSchema,
  max: NumberRequiredSchema.min(0, "errors.required"),
  start: TimeRequiredSchema,
  end: TimeRequiredSchema.test(
    "is-greater",
    "errors.invalidDate",
    timeTest("start"),
  ),
  startSaturday: TimeSchema,
  endSaturday: TimeSchema.test(
    "is-greater",
    "errors.invalidDate",
    timeTest("startSaturday"),
  ),
  startSunday: TimeSchema,
  endSunday: TimeSchema.test(
    "is-greater",
    "errors.invalidDate",
    timeTest("startSunday"),
  ),
});

export const ManageStatusGroupSchema = yup.object().shape({
  active: BooleanSchema,
  name: StringRequiredSchema,
  roles: NumberArraySchema,
  channels: NumberArraySchema,
});

export const ManageStatusSchema = yup.object().shape({
  active: BooleanSchema,
  statusGroup: NumberRequiredSchema,
  name: StringRequiredSchema,
  code: StringRequiredSchema,
  dialerCode: NumberRequiredSchema,
  type: NumberRequiredSchema,
  hasApproach: BooleanSchema,
  hasChat: BooleanSchema,
  hasRedial: BooleanSchema,
  hasSchedule: BooleanSchema,
  hasVoice: BooleanSchema,
  isApproach: BooleanSchema,
  isOrder: BooleanSchema,
  mustValidate: BooleanSchema,
  obsIsRequired: BooleanSchema,
  mustValidateOrder: BooleanSchema,
});

export const ManageUserProjectSchema = yup.object().shape({
  projects: NumberArrayRequiredSchema,
});

export const ManageUserSchema = yup
  .object()
  .shape({
    active: BooleanSchema,
    name: StringRequiredSchema,
    accessLogin: StringRequiredSchema,
    phoneLogin: AlphanumericSchema,
    email: EmailSchema,
    registrationNumber: CPFSchema,
    ddd: StringSchema,
    phone: PhoneSchema,
    birth: DateMaxSchema,
    project: NumberRequiredSchema,
    supervisor: NumberRequiredSchema,
    permission: NumberRequiredSchema,
    callNumbers: NumberSchema,
    urlWebPhone: StringSchema,
  })
  .test(({ ddd, phone }, ctx) => {
    if (!!ddd && !phone)
      return new yup.ValidationError("errors.required", phone, "phone");
    else if (!!phone && !ddd)
      return new yup.ValidationError("errors.required", ddd, "ddd");
    else return true;
  });

export const MergeAttendanceSchema = yup.object().shape({
  name: StringSchema,
  cpf: CPFSchema,
  ddd: StringSchema,
  phone: StringSchema,
});

export const NewPasswordSchema = yup.object().shape({
  password: StringRequiredSchema,
  newPassword: PasswordSchema,
  confirmPassword: StringRequiredSchema.oneOf(
    [yup.ref("newPassword")],
    "errors.passwordDoesNotMatch",
  ),
});

export const RedialAttendanceSchema = yup.object().shape({
  status: NumberRequiredSchema,
  observation: StringSchema,
  ddd: StringSchema,
  phone: StringRequiredSchema,
});

export const ScheduleAttendanceSchema = yup.object().shape({
  status: NumberRequiredSchema,
  observation: StringSchema,
  date: DateRequiredSchema,
  hour: TimeRequiredSchema,
  ddd: StringRequiredSchema,
  phone: PhoneRequiredSchema,
});

export const SelectProjectSchema = yup.object().shape({
  project: NumberRequiredSchema,
  softphone: NumberSchema,
});

export const SendHSMSchema = yup.object().shape({
  template: NumberRequiredSchema,
  mailingName: StringSchema,
  userName: StringSchema,
  customField1: StringSchema,
  customField2: StringSchema,
  customField3: StringSchema,
  customField4: StringSchema,
  customField5: StringSchema,
  customField6: StringSchema,
  customField7: StringSchema,
  customField8: StringSchema,
  customField9: StringSchema,
  customField10: StringSchema,
});

export const SendHSMTestSchema = yup.object().shape({
  phone: PhoneRequiredSchema,
});

export const SignInSchema = yup.object().shape({
  login: StringRequiredSchema,
  password: StringRequiredSchema,
});

export const SignUpSchema = yup.object().shape({
  name: StringRequiredSchema,
  phone: PhoneSchema,
  email: EmailRequiredSchema,
  password: PasswordSchema,
});

export const TransferAssistedAttendanceSchema = yup.object().shape({
  step: NumberRequiredSchema,
  transferBy: StringSchema,
  branch: StringRequiredSchema,
});

export const TransferAttendanceSchema = yup.object().shape({
  isVoice: BooleanRequiredSchema,
  step: NumberRequiredSchema,
  transferBy: NumberSchema.when("isVoice", ([isVoice]) =>
    !isVoice ? NumberRequiredSchema : NumberSchema,
  ),
  branch: StringSchema.when("isVoice", ([isVoice]) =>
    isVoice ? StringRequiredSchema : StringSchema,
  ),
});

export const UserImportSchema = yup.object().shape({
  project: NumberRequiredSchema,
  file: MixedRequiredSchema,
});

export const WelcomeSchema = yup.object().shape({
  company: StringRequiredSchema,
  organization: StringRequiredSchema,
});
